import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';

interface NodeBrowserProps {
  nodes: any[];
  selectedNodes: any[];
  onFocusNode?: (nodeId: string) => void;
  onNodesChange?: (changes: any[]) => void;
  onClearSelection: () => void;
  onSelectAll: () => void;
}

export const NodeBrowser: React.FC<NodeBrowserProps> = ({
  nodes,
  selectedNodes,
  onFocusNode,
  onNodesChange,
  onClearSelection,
  onSelectAll
}) => {
  const { darkMode } = useTheme();
  const [showBrowser, setShowBrowser] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [browserMode, setBrowserMode] = useState<'search' | 'selected'>('search');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const browserRef = useRef<HTMLDivElement>(null);

  // Searchable nodes logic
  const searchableNodes = useMemo(() => {
    return nodes.map(node => ({
      id: node.id,
      label: node.data?.label || node.data?.toolName || node.data?.personaName || node.id,
      type: node.type || 'unknown'
    }));
  }, [nodes]);

  // Search logic
  const searchResults = useMemo(() => {
    if (!searchTerm.trim()) return [];
    
    const term = searchTerm.toLowerCase();
    return searchableNodes
      .filter(item => {
        const label = item.label.toLowerCase();
        const type = item.type.toLowerCase();
        return label.includes(term) || type.includes(term);
      })
      .slice(0, 10);
  }, [searchTerm, searchableNodes]);

  // Auto-switch browser mode
  useEffect(() => {
    if (showBrowser) {
      if (selectedNodes.length > 0 && !searchTerm.trim()) {
        setBrowserMode('selected');
      } else if (searchTerm.trim()) {
        setBrowserMode('search');
      }
    }
  }, [selectedNodes.length, searchTerm, showBrowser]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (browserRef.current && 
          !browserRef.current.contains(event.target as Node) &&
          searchInputRef.current &&
          !searchInputRef.current.contains(event.target as Node)) {
        setShowBrowser(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleNodeSelection = (nodeId: string, selected: boolean) => {
    if (onNodesChange) {
      onNodesChange([{
        id: nodeId,
        type: 'select' as const,
        selected: selected
      }]);
    }
  };

  return (
    <div className="flex items-center space-x-1 flex-1 max-w-xs relative" ref={browserRef}>
      <div className="relative flex-1">
        <input
          ref={searchInputRef}
          type="text"
          placeholder={selectedNodes.length > 0 ? `${selectedNodes.length} selected` : "Search nodes..."}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => setShowBrowser(true)}
          className={`w-full px-2 py-0.5 pr-6 text-xs rounded-md border transition-all duration-200 focus:scale-105 ${
            selectedNodes.length > 0 
              ? darkMode 
                ? 'bg-blue-900/60 border-blue-600/50 text-blue-100 placeholder-blue-300/70' 
                : 'bg-blue-50/80 border-blue-300/60 text-blue-900 placeholder-blue-600/70'
              : darkMode 
                ? 'bg-gray-700/60 border-gray-600/50 text-white placeholder-gray-400' 
                : 'bg-white/80 border-gray-300/60 text-gray-900 placeholder-gray-500'
          } focus:outline-none focus:ring-1 focus:ring-green-400/50 backdrop-blur-sm`}
        />
        
        {/* Clear/Close button with hover effect */}
        {(searchTerm || selectedNodes.length > 0) && (
          <button
            onClick={() => {
              if (searchTerm) {
                setSearchTerm('');
              } else {
                onClearSelection();
              }
              setShowBrowser(false);
            }}
            className={`absolute right-1 top-1/2 transform -translate-y-1/2 p-0.5 rounded-full transition-all duration-200 hover:scale-110 ${
              darkMode ? 'hover:bg-gray-600/80 text-gray-400 hover:text-white' : 'hover:bg-gray-200/80 text-gray-500 hover:text-gray-700'
            }`}
          >
            ✕
          </button>
        )}
        
        {/* Browser popup with modern styling */}
        {showBrowser && (
          <div className={`absolute top-full left-0 right-0 mt-1 rounded-xl shadow-2xl border z-50 max-h-80 overflow-hidden backdrop-blur-md ${
            darkMode 
              ? 'bg-gray-800/95 border-gray-600/50 shadow-black/50' 
              : 'bg-white/95 border-gray-200/50 shadow-gray-400/30'
          }`}>
            {/* Header with gradient */}
            <div className={`px-3 py-1.5 border-b flex items-center justify-between ${
              darkMode 
                ? 'border-gray-600/50 bg-gradient-to-r from-gray-700/50 to-gray-800/50' 
                : 'border-gray-200/50 bg-gradient-to-r from-gray-50/50 to-gray-100/50'
            }`}>
              <span className="text-xs font-medium">
                {browserMode === 'search' 
                  ? `${searchResults.length} result${searchResults.length !== 1 ? 's' : ''}`
                  : `${selectedNodes.length} selected`
                }
              </span>
              <button
                onClick={() => {
                  setShowBrowser(false);
                  setSearchTerm('');
                }}
                className="text-xs px-1.5 py-0.5 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200"
              >
                ✕
              </button>
            </div>

            {/* Content with compact styling */}
            <div className="max-h-64 overflow-y-auto">
              {browserMode === 'search' ? (
                searchResults.map((node) => (
                  <div key={node.id} className="px-3 py-1.5 text-xs flex items-center space-x-2 hover:bg-gray-50/50 dark:hover:bg-gray-700/50 transition-colors duration-150">
                    <input
                      type="checkbox"
                      checked={selectedNodes.some(n => n.id === node.id)}
                      onChange={(e) => handleToggleNodeSelection(node.id, e.target.checked)}
                      className="w-3 h-3"
                    />
                    <button
                      onClick={() => onFocusNode?.(node.id)}
                      className="px-1 py-0.5 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors duration-200"
                    >
                      📍
                    </button>
                    <span className="flex-1 truncate">{node.label}</span>
                  </div>
                ))
              ) : (
                selectedNodes.map((node) => (
                  <div key={node.id} className="px-3 py-1.5 text-xs flex items-center space-x-2 hover:bg-gray-50/50 dark:hover:bg-gray-700/50 transition-colors duration-150">
                    <input
                      type="checkbox"
                      checked={true}
                      onChange={() => handleToggleNodeSelection(node.id, false)}
                      className="w-3 h-3"
                    />
                    <button
                      onClick={() => onFocusNode?.(node.id)}
                      className="px-1 py-0.5 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors duration-200"
                    >
                      📍
                    </button>
                    <span className="flex-1 truncate">{node.data?.label || node.id}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
      
      <button 
        onClick={onSelectAll} 
        className={`px-1.5 py-0.5 text-xs rounded-md whitespace-nowrap transition-all duration-200 hover:scale-105 ${
          darkMode 
            ? 'bg-gray-700/60 hover:bg-gray-600 text-gray-300 hover:text-white' 
            : 'bg-gray-200/60 hover:bg-gray-300 text-gray-700 hover:text-gray-900'
        }`}
      >
        All
      </button>
    </div>
  );
};
